/**
 * Attributi della squadra (V30): la rosa per attributi attivi, una scheda per
 * famiglia.
 *
 * Ogni scheda e una card su sfondo grigio con la tabella della famiglia: la
 * maglia e il nome del giocatore, l'ultimo valore di ogni attributo nel
 * colore della sua fascia e la media intera come nella rosa. Ogni famiglia
 * elenca tutta la rosa: il portiere ha i suoi tecnici, il terzino le sue
 * parate. Le intestazioni ordinano la tabella.
 *
 * «Nuova sessione» rende le celle modificabili, con l'ultimo valore in
 * trasparenza: la sessione vale per tutte le famiglie, e il salvataggio
 * aggiunge una misurazione con la data scelta, non sovrascrive l'ultima.
 * Si misura solo chi e in rosa oggi. La famiglia aperta sta nella query
 * string.
 */

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { MinusIcon, OctagonXIcon, PlusIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  FAMIGLIE_ATTRIBUTO,
  type FamigliaAttributo,
  puo,
  sessioneMisurazioniSchema,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { CampoData } from '../comuni/CampoData.js';
import { Corpo } from '../comuni/Corpo.js';
import { Ordinabile } from '../comuni/Ordinabile.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { dataBreve, messaggio, useDati } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { coloreValore } from '../giocatori/colori-stato.js';
import { oggi } from '../giocatori/elenchi.js';
import { useContesto } from '../navigazione/contesto.js';
import { Maglia } from '../comuni/Maglia.js';
import { Sezione } from '../statistiche/PaginaStatistiche.js';
import type { AttributoColonna, GiocatoreMatriceAttributi, MatriceAttributi } from './tipi.js';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

type Bozza = Record<string, string>;

const chiave = (giocatoreId: string, attributoId: string) => `${giocatoreId}|${attributoId}`;

/** `giocatore`, `numero`, `media` o l'id di un attributo. */
interface Ordine {
  colonna: string;
  verso: 'asc' | 'desc';
}

const PER_COGNOME: Ordine = { colonna: 'giocatore', verso: 'asc' };

const perNome = new Intl.Collator('it', { sensitivity: 'base' });

/** Media intera degli ultimi valori della famiglia, `null` senza misure. */
function media(g: GiocatoreMatriceAttributi, attributi: AttributoColonna[]): number | null {
  const valori = attributi.flatMap((a) => (g.valori[a.id] ? [g.valori[a.id]!.valore] : []));
  if (valori.length === 0) return null;
  return Math.round(valori.reduce((t, v) => t + v, 0) / valori.length);
}

function ordina(
  giocatori: GiocatoreMatriceAttributi[],
  attributi: AttributoColonna[],
  { colonna, verso }: Ordine,
): GiocatoreMatriceAttributi[] {
  const perCognome = (a: GiocatoreMatriceAttributi, b: GiocatoreMatriceAttributi) =>
    perNome.compare(a.cognome, b.cognome) || perNome.compare(a.nome, b.nome);
  if (colonna === 'giocatore') {
    return [...giocatori].sort((a, b) => (verso === 'asc' ? 1 : -1) * perCognome(a, b));
  }
  const valore = (g: GiocatoreMatriceAttributi): number | null =>
    colonna === 'numero'
      ? g.numeroMaglia
      : colonna === 'media'
        ? media(g, attributi)
        : (g.valori[colonna]?.valore ?? null);
  // Chi non ha il valore va in fondo in entrambi i versi.
  return [...giocatori].sort((a, b) => {
    const x = valore(a);
    const y = valore(b);
    if (x === null || y === null) return x === y ? perCognome(a, b) : x === null ? 1 : -1;
    return (verso === 'asc' ? x - y : y - x) || perCognome(a, b);
  });
}

export function AttributiSquadra() {
  const { ss } = useParams<{ ss: string }>();
  const { utente } = useContesto();
  const [params, setParams] = useSearchParams();
  const { dati, errore, ricarica } = useDati<MatriceAttributi>(
    `/squadre-stagione/${ss}/valutazioni/attributi`,
  );
  const [ordine, setOrdine] = useState<Ordine>(PER_COGNOME);
  const [sessione, setSessione] = useState(false);
  const [data, setData] = useState(oggi());
  const [bozza, setBozza] = useState<Bozza>({});
  const [erroreAzione, setErroreAzione] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (!dati) {
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );
  }

  const famiglie = FAMIGLIE_ATTRIBUTO.filter((f) => dati.attributi.some((a) => a.famiglia === f));
  const richiesta = params.get('famiglia') as FamigliaAttributo | null;
  const famiglia = richiesta && famiglie.includes(richiesta) ? richiesta : famiglie[0];
  const misurabili = dati.puoScrivere && dati.giocatori.some((g) => g.puoScrivere);

  const rilevazioni = Object.entries(bozza).flatMap(([k, raw]) => {
    if (raw.trim() === '') return [];
    const [giocatoreId, attributoId] = k.split('|');
    if (!giocatoreId || !attributoId) return [];
    return [{ giocatoreId, attributoId, valore: Number(raw) }];
  });

  // Il conto dei valori nuovi sta sulla scheda: si vedono anche le famiglie non aperte.
  const famigliaDi = new Map(dati.attributi.map((a) => [a.id, a.famiglia]));
  const compilatePer = new Map<FamigliaAttributo, number>();
  for (const { attributoId } of rilevazioni) {
    const f = famigliaDi.get(attributoId);
    if (f) compilatePer.set(f, (compilatePer.get(f) ?? 0) + 1);
  }

  const scegliFamiglia = (f: string) => {
    setOrdine(PER_COGNOME);
    setParams({ famiglia: f }, { replace: true });
  };

  const chiudiSessione = () => {
    setSessione(false);
    setBozza({});
    setErroreAzione(null);
  };

  const salva = async () => {
    const esito = sessioneMisurazioniSchema.safeParse({ data, rilevazioni });
    if (!esito.success) {
      setErroreAzione(esito.error.issues[0]?.message ?? 'Compila almeno una cella.');
      return;
    }
    setErroreAzione(null);
    setInCorso(true);
    try {
      await chiama(`/squadre-stagione/${ss}/valutazioni/misurazioni`, {
        metodo: 'POST',
        corpo: esito.data,
      });
      await ricarica();
      chiudiSessione();
      toast.success('Misurazioni salvate.');
    } catch (err) {
      setErroreAzione(messaggio(err));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div>
      <Testata
        titolo="Attributi"
        voce="attributi"
        azione={
          misurabili &&
          !sessione &&
          famiglia && <PulsanteNuovo etichetta="Nuova sessione" onClick={() => setSessione(true)} />
        }
      />
      <Corpo className="flex flex-col gap-4">
        {dati.giocatori.length === 0 || !famiglia ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{famiglia ? 'Rosa vuota' : 'Nessun attributo attivo'}</EmptyTitle>
              <EmptyDescription>
                {famiglia
                  ? 'Aggiungi i tesserati dalla rosa per rilevare gli attributi.'
                  : 'Gli attributi si attivano dal catalogo, in amministrazione.'}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            <Tabs value={famiglia} onValueChange={scegliFamiglia}>
              <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none]">
                {famiglie.map((f) => (
                  <TabsTrigger key={f} value={f}>
                    <IconaFamiglia famiglia={f} aria-hidden />
                    {ETICHETTA_FAMIGLIA_ATTRIBUTO[f]}
                    {sessione && (compilatePer.get(f) ?? 0) > 0 && (
                      <Badge
                        className="bg-modificato text-background tabular-nums"
                        title="Valori nuovi in questa famiglia"
                      >
                        {compilatePer.get(f)}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {sessione && (
              <BarraSessione
                data={data}
                onData={setData}
                compilate={rilevazioni.length}
                errore={erroreAzione}
                inCorso={inCorso}
                onAnnulla={chiudiSessione}
                onSalva={() => void salva()}
              />
            )}

            <TabellaFamiglia
              famiglia={famiglia}
              attributi={dati.attributi.filter((a) => a.famiglia === famiglia)}
              giocatori={dati.giocatori}
              ordine={ordine}
              onOrdine={setOrdine}
              sessione={sessione}
              bozza={bozza}
              onCambio={(k, v) => setBozza((b) => ({ ...b, [k]: v }))}
              apribile={(g) => g.inRosa || puo(utente.assegnazioni, 'tesseramento.gestisci', ss)}
            />
          </>
        )}
      </Corpo>
    </div>
  );
}

/** La data della sessione, il conto delle celle compilate e le due azioni. */
function BarraSessione({
  data,
  onData,
  compilate,
  errore,
  inCorso,
  onAnnulla,
  onSalva,
}: {
  data: string;
  onData: (data: string) => void;
  compilate: number;
  errore: string | null;
  inCorso: boolean;
  onAnnulla: () => void;
  onSalva: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field className="w-auto">
          <FieldLabel htmlFor="sessione-data">Data della sessione</FieldLabel>
          <CampoData id="sessione-data" valore={data} onCambia={onData} />
        </Field>
        <Nota className="min-w-48 flex-1">
          Ogni valore diventa una nuova misurazione con data e autore, anche passando da una
          famiglia all’altra: non sovrascrive l’ultima. Le celle vuote restano come sono.
        </Nota>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onAnnulla} disabled={inCorso}>
            Annulla
          </Button>
          <PulsanteInvio
            type="button"
            onClick={onSalva}
            inCorso={inCorso}
            disabled={compilate === 0}
          >
            Salva
            {compilate > 0 && (
              <Badge variant="secondary" className="tabular-nums">
                {compilate}
              </Badge>
            )}
          </PulsanteInvio>
        </div>
      </div>
      {errore && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Misurazioni non salvate</AlertTitle>
          <AlertDescription>{errore}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function TabellaFamiglia({
  famiglia,
  attributi,
  giocatori,
  ordine,
  onOrdine,
  sessione,
  bozza,
  onCambio,
  apribile,
}: {
  famiglia: FamigliaAttributo;
  attributi: AttributoColonna[];
  giocatori: GiocatoreMatriceAttributi[];
  ordine: Ordine;
  onOrdine: (ordine: Ordine) => void;
  sessione: boolean;
  bozza: Bozza;
  onCambio: (chiave: string, valore: string) => void;
  apribile: (g: GiocatoreMatriceAttributi) => boolean;
}) {
  // Il cognome parte dalla A, i numeri di maglia dal piu basso, i valori dal piu alto.
  const intestazione = (colonna: string, etichetta: string, versoIniziale: Ordine['verso']) => (
    <Ordinabile
      attiva={ordine.colonna === colonna}
      verso={ordine.verso}
      onClick={() =>
        onOrdine({
          colonna,
          verso:
            ordine.colonna === colonna ? (ordine.verso === 'asc' ? 'desc' : 'asc') : versoIniziale,
        })
      }
    >
      {etichetta}
    </Ordinabile>
  );

  return (
    <Sezione
      titolo={ETICHETTA_FAMIGLIA_ATTRIBUTO[famiglia]}
      icona={<IconaFamiglia famiglia={famiglia} aria-hidden className="size-5" />}
      conteggio={giocatori.length}
    >
      {giocatori.length === 0 ? (
        <StatoVuoto titolo="Nessun giocatore in rosa" />
      ) : (
        // La tabella scorre dentro la card, in orizzontale e in verticale:
        // intestazioni e colonna dei giocatori restano ferme.
        <div
          className={cn(
            'overflow-hidden rounded-lg border bg-card',
            // Alta quanto la finestra, tolte testata e barra del telefono; in sessione
            // la barra della data sta sopra la card e la tabella si accorcia.
            sessione
              ? '[&>[data-slot=table-container]]:max-h-[calc(100dvh-var(--altezza-testata,0px)-var(--altezza-barra,0px)-19rem)]'
              : '[&>[data-slot=table-container]]:max-h-[calc(100dvh-var(--altezza-testata,0px)-var(--altezza-barra,0px)-14rem)]',
          )}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={cn(INTESTAZIONE, 'left-0 z-30')}>
                  {intestazione('numero', 'N.', 'asc')}
                  <span className="text-muted-foreground"> · </span>
                  {intestazione('giocatore', 'Giocatore', 'asc')}
                </TableHead>
                <TableHead className={cn(INTESTAZIONE, 'text-center')}>
                  {intestazione('media', 'Media', 'desc')}
                </TableHead>
                {attributi.map((a) => (
                  <TableHead
                    key={a.id}
                    className={cn(INTESTAZIONE, 'min-w-20 text-center whitespace-normal')}
                  >
                    {intestazione(a.id, a.nome, 'desc')}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordina(giocatori, attributi, ordine).map((g) => {
                const m = media(g, attributi);
                return (
                  <TableRow key={g.id} className={cn(!g.inRosa && 'text-muted-foreground')}>
                    <TableCell className="sticky left-0 z-10 bg-card py-1">
                      <Giocatore giocatore={g} apribile={apribile(g)} />
                    </TableCell>
                    <TableCell className="py-1 text-center">
                      <Valore valore={m} titolo="Media della famiglia" grande />
                    </TableCell>
                    {attributi.map((a) => {
                      const ultima = g.valori[a.id];
                      const k = chiave(g.id, a.id);
                      return (
                        <TableCell key={a.id} className="py-1 text-center">
                          {sessione && g.puoScrivere ? (
                            <CampoMisura
                              etichetta={`${a.nome} ${g.cognome} ${g.nome}`}
                              ultimo={ultima?.valore ?? null}
                              valore={bozza[k] ?? ''}
                              onCambio={(v) => onCambio(k, v)}
                            />
                          ) : (
                            <Valore
                              valore={ultima?.valore ?? null}
                              titolo={ultima ? `Rilevato il ${dataBreve(ultima.data)}` : undefined}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Sezione>
  );
}

/**
 * Le intestazioni ferme in cima mentre la tabella scorre; la riga sotto la
 * disegna il bordo del `tr`, che nella cella ferma non segue: la rifà un
 * filo in fondo alla cella.
 */
const INTESTAZIONE =
  'sticky top-0 z-20 bg-card after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-border';

/** Il punto di partenza di «+» e «−» per chi non e mai stato misurato. */
const PARTENZA = 60;

const nellaScala = (v: number) => Math.min(100, Math.max(1, v));

/**
 * Il valore nuovo di una cella in sessione. Le freccine del browser partono
 * da vuoto, cioe da 1: qui il campo e di testo e «+» e «−», a destra uno
 * sopra l'altro, come le frecce della tastiera, partono dall'ultimo valore
 * mostrato in trasparenza. Una cella compilata si distingue dalle altre e
 * ha la «x» per svuotarla, a cavallo dell'angolo in alto a sinistra.
 */
function CampoMisura({
  etichetta,
  ultimo,
  valore,
  onCambio,
}: {
  etichetta: string;
  ultimo: number | null;
  valore: string;
  onCambio: (valore: string) => void;
}) {
  const modificato = valore !== '';
  const passo = (delta: number) => {
    const base =
      modificato && Number.isFinite(Number(valore)) ? Number(valore) : (ultimo ?? PARTENZA);
    onCambio(String(nellaScala(base + delta)));
  };
  return (
    <InputGroup
      data-modificato={modificato || undefined}
      className={cn(
        'mx-auto w-20',
        modificato &&
          'border-modificato bg-avviso text-avviso-foreground ring-3 ring-modificato/30',
      )}
    >
      {modificato && (
        // A cavallo dell'angolo in alto a sinistra, fuori dalla cella di testo;
        // sotto la colonna fissa dei giocatori quando la tabella scorre.
        <Button
          type="button"
          size="icon-xs"
          className="absolute top-0 left-0 z-1 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-modificato text-background hover:bg-modificato/80"
          tabIndex={-1}
          aria-label={`Svuota ${etichetta}`}
          title="Svuota"
          onClick={() => onCambio('')}
        >
          <XIcon className="size-3" />
        </Button>
      )}
      <InputGroupInput
        inputMode="numeric"
        autoComplete="off"
        maxLength={3}
        className={cn('text-center tabular-nums', modificato && 'font-bold')}
        aria-label={etichetta}
        placeholder={ultimo != null ? String(ultimo) : '—'}
        value={valore}
        onChange={(e) => onCambio(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            passo(e.key === 'ArrowUp' ? 1 : -1);
          }
        }}
      />
      <InputGroupAddon align="inline-end" className="flex-col gap-0 py-0">
        <InputGroupButton
          size="icon-xs"
          className="h-3.5"
          tabIndex={-1}
          aria-label={`Aumenta ${etichetta}`}
          onClick={() => passo(1)}
        >
          <PlusIcon className="size-3" />
        </InputGroupButton>
        <InputGroupButton
          size="icon-xs"
          className="h-3.5"
          tabIndex={-1}
          aria-label={`Diminuisci ${etichetta}`}
          onClick={() => passo(-1)}
        >
          <MinusIcon className="size-3" />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

/** La maglia e il nome, che apre gli attributi della scheda giocatore. */
function Giocatore({
  giocatore: g,
  apribile,
}: {
  giocatore: GiocatoreMatriceAttributi;
  apribile: boolean;
}) {
  const nome = (
    <span className="flex min-w-0 flex-col">
      {/* Del nome la sola iniziale, per lasciare spazio agli attributi. */}
      <span className="font-semibold whitespace-normal" title={`${g.cognome} ${g.nome}`}>
        {`${g.cognome}\u00a0${g.nome.charAt(0)}.`}
      </span>
      {!g.inRosa && <span className="text-xs">Uscito dalla rosa</span>}
    </span>
  );
  return (
    <span className="flex min-w-32 items-center gap-2">
      <span className={cn(!g.inRosa && 'opacity-60')}>
        <Maglia ruolo={g.ruoloPreferito} numero={g.numeroMaglia} compatta />
      </span>
      {apribile ? (
        <Link
          to={`/giocatori/${g.id}/attributi`}
          className="min-w-0 underline-offset-4 hover:underline"
        >
          {nome}
        </Link>
      ) : (
        nome
      )}
    </span>
  );
}

/** Un valore da 1 a 100 nel colore della sua fascia, «—» senza misura. */
function Valore({
  valore,
  titolo,
  grande,
}: {
  valore: number | null;
  titolo?: string;
  grande?: boolean;
}) {
  if (valore === null) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge
      title={titolo}
      className={cn('min-w-9 tabular-nums', grande && 'text-sm font-bold', coloreValore(valore))}
    >
      {valore}
    </Badge>
  );
}
